/** @jsx jsx */
import React from 'react';

import { jsx } from '@emotion/react';
import styled from '@emotion/styled';
import { Box, ButtonProps, Text } from '@opendoor/bricks/core';
import Button from '@opendoor/bricks/core/Button';
import Link from '@opendoor/bricks/core/Link';
import { A11yProps } from '@opendoor/bricks/system/a11y.types';
import { useTheme } from '@opendoor/bricks/system/theme';
import { fontSizesNamed } from '@opendoor/bricks/theme/eero';

const ThemedButton: typeof Button = (props: ButtonProps) => {
  const { name } = useTheme();
  if (name === 'novo') {
    return (
      <Button size="lg" fontSize="22px" minHeight={['65px', null, '74px', '82px']} {...props} />
    );
  }
  return <Button {...props} />;
};

export interface IAccountFormControlsProps {
  /**
   * Name of the account form in analytics events, should be unique within the app
   * Convention: {appName}-{accountFormName}
   */
  analyticsName: string;
  actionText?: string;
  loading?: boolean;
  showForgotPassword?: boolean;
  showPasswordlessLogin?: boolean;
  onRequestPasswordlessLogin?: () => void;
  isRequestingPasswordlessLogin?: boolean;
  disabled?: boolean;
  openForgotPasswordInNewTab?: boolean;
  submitA11yProps: A11yProps<'button'>;
}

const NO_OP = () => undefined;

const CtaButtonWrapper = styled.div`
  margin-top: 30px;
  margin-bottom: 15px;
`;

const ForgotPasswordWrapper = styled.p`
  font-size: ${fontSizesNamed.s0};
  text-align: center;
`;

export default class AccountFormControls extends React.Component<IAccountFormControlsProps> {
  public static defaultProps: Partial<IAccountFormControlsProps> = {
    actionText: 'Submit',
    showForgotPassword: true,
  };

  public render() {
    const {
      analyticsName,
      loading,
      actionText,
      showForgotPassword,
      showPasswordlessLogin,
      onRequestPasswordlessLogin,
      isRequestingPasswordlessLogin,
      disabled,
      openForgotPasswordInNewTab,
    } = this.props;

    return (
      <div>
        <CtaButtonWrapper>
          <ThemedButton
            loading={loading}
            onClick={NO_OP}
            type="submit"
            disabled={disabled}
            analyticsName={`${analyticsName}-submit`}
            {...this.props.submitA11yProps}
          >
            {actionText}
          </ThemedButton>
        </CtaButtonWrapper>
        {showForgotPassword && (
          <ForgotPasswordWrapper>
            <Link
              fontSize="s1"
              fontWeight="semibold"
              href="/reset-password"
              target={openForgotPasswordInNewTab ? '_blank' : '_self'}
              analyticsName={`${analyticsName}-forgot-password`}
              aria-label={
                openForgotPasswordInNewTab
                  ? 'Opens a new window with the forgot password page'
                  : 'Opens the forgot password page'
              }
            >
              Forgot your password?
            </Link>
          </ForgotPasswordWrapper>
        )}
        {showPasswordlessLogin && (
          <Box
            data-testid="passwordless-login"
            my={9}
            pt={9}
            borderTop="1px solid"
            borderTopColor="neutrals40"
          >
            <Text fontSize="s1" pb={7} textAlign="left">
              Tired of reseting your password? We can email you a link that will simply log you into
              your account.
            </Text>
            <Button
              variant="secondary"
              loading={isRequestingPasswordlessLogin}
              analyticsName={`${analyticsName}-email-login-link`}
              aria-label={null}
              onClick={onRequestPasswordlessLogin}
            >
              Email login link
            </Button>
          </Box>
        )}
      </div>
    );
  }
}
